import { useCallback, useMemo } from 'react';
import {
    cleanNumericInput,
    toMonthlyIncome,
    toAnnualIncome,
    currencyOptions,
    MAX_MONTHLY_INCOME,
    MAX_ANNUAL_INCOME,
} from '../../../utils/income-utils';
import { generateErrorMessage } from '../../../utils/error-message';
import { isNewErrorMessagingFlagActive } from '../../../../src/utils/error-message-flag';
import SessionStorage from '../../../utils/SessionStorage';

const useMonthlyIncomeHandling = () => {
    const updateErrorMessaging = isNewErrorMessagingFlagActive && SessionStorage.tryGetObject('grossIncomeEnabled', false);
    const errorMessages = useMemo(
        () =>
            ({
                VAL_TOO_SMALL: {
                    monthly: 'Monthly Income must be at least 3 digits',
                    yearly: 'Yearly Income must be at least $1200',
                },
                EXCEEDS_MIN: {
                    monthly: 'Monthly Income must be at least 3 digits',
                    yearly: 'Yearly Income must be at least $1200',
                },
                EXCEEDS_MAX: {
                    monthly:
                        'Monthly Income exceeds amount allowed in this field',
                    yearly: 'Yearly Income exceeds amount allowed in this field',
                },
                MISSING_VALUE: {
                    monthly: updateErrorMessaging ? generateErrorMessage('Income', 'missingValue') : generateErrorMessage('Total Monthly Income', 'missingValue'),
                    yearly: updateErrorMessaging ? generateErrorMessage('Income', 'missingValue') : generateErrorMessage('Total Yearly Income', 'missingValue'),
                },
                DEFAULT: { 
                    monthly: generateErrorMessage('Total Monthly Income'),
                    yearly: generateErrorMessage('Total Yearly Income'),
                },
            } as const),
        [],
    );

    const incomeLimits = useMemo(
        () =>
            ({
                lengthRequirement: {
                    monthly: 3,
                    yearly: 4,
                },
                min: {
                    monthly: 100,
                    yearly: 1200,
                },
                max: {
                    monthly: MAX_MONTHLY_INCOME,
                    yearly: MAX_ANNUAL_INCOME,
                },
                popupThreshold: {
                    monthly: 500,
                    yearly: 6000,
                },
            } as const),
        [],
    );

    const convertValue = useCallback(
        (
            valueToConvert: string | number,
            convertFrom: 'yearly' | 'monthly',
        ): string => {
            if (typeof valueToConvert === 'string') {
                valueToConvert = Number(cleanNumericInput(valueToConvert));
            }
            const toReturn =
                convertFrom === 'yearly'
                    ? toMonthlyIncome(valueToConvert)
                    : toAnnualIncome(valueToConvert);
            return toReturn > 0
                ? toReturn.toLocaleString('en-US', currencyOptions)
                : '';
        },
        [cleanNumericInput, toMonthlyIncome, toAnnualIncome],
    );

    const convertToAnnual = (valueToConvert: string | number): string => {
        if (typeof valueToConvert === 'string') {
            valueToConvert = Number(cleanNumericInput(valueToConvert));
        }
        const toReturn = toAnnualIncome(valueToConvert);
        return toReturn > 0
            ? toReturn.toLocaleString('en-US', currencyOptions)
            : '';
    }

    return {
        incomeLimits,
        convertValue,
        convertToAnnual,
        errorMessages,
    };
};

export default useMonthlyIncomeHandling;
